import type { OvpClient } from '@bldsoft/cappie/api/ovp'
import type { API } from '@setplex/tria-api'
import type { AdapterDefaults } from '../../index.h'
import { payload } from '../../result'
import { withTotal } from '../../utils/paginatedList'
import { format as contentFormat } from '../content/format'
import type { ApiContent } from '../content/index.h'
import { format as tvShowFormat } from '../tvshows/format'
import { ApiTvShow } from '../tvshows/index.h'
import { format, formatCreatorTag } from './format'
import type { ApiCreator, ApiCreatorTag } from './index.h'

export function use(
  client: OvpClient,
  creators: API['creators'],
  _api: API,
  _defaults: AdapterDefaults
): void {
  // GET /api/web/creators
  creators.base.getManyFx.use(
    async ({
      limit,
      offset,
      by = _defaults.sortBy,
      order = _defaults.sortOrder,
      q,
    }) => {
      const data = payload<{ results: ApiCreator[] }>(
        await client.get.api.web.creators({
          query: { limit, offset, sortBy: by, sortOrder: order, q },
        })
      )

      if (!data) {
        throw new Error('Empty answer in creators.base.getManyFx')
      }

      return (data.results || []).map(format)
    }
  )

  // GET /api/web/creators/{id}
  creators.base.getOneFx.use(async ({ id }) => {
    const data = payload<ApiCreator>(await client.get.api.web.creators(id)())

    if (!data) {
      throw new Error('Empty answer in creators.base.getOneFx')
    }

    return format(data)
  })

  // GET /api/web/creators/{id}/content
  creators.base.getContentFx.use(async ({ id, limit, offset, by, order }) => {
    const data = payload<{ results: ApiContent[]; total: number }>(
      await client.get.api.web.creators(id).content({
        query: { limit, offset, sortBy: by, sortOrder: order },
      })
    )

    if (!data) {
      throw new Error('Empty answer in creators.base.getContentFx')
    }

    const results = (data.results || []).map(contentFormat)

    return withTotal(results, data.total)
  })

  // GET /api/web/creators/{id}/tv-shows
  creators.base.getTvShowsFx.use(async ({ id, limit, offset, by, order }) => {
    const data = payload<{ results: ApiTvShow[]; total: number }>(
      await client.get.api.web.creators(id).tvShows({
        query: { limit, offset, sortBy: by, sortOrder: order },
      })
    )

    if (!data) {
      throw new Error('Empty answer in creators.base.getTvShowsFx')
    }

    const results = (data.results || []).map(tvShowFormat)

    return withTotal(results, data.total)
  })

  // GET /api/web/creators/{id}/content-tags
  creators.base.getContentTagsFx.use(
    async ({ id, limit, offset, by, order, withContent, contentCount }) => {
      const data = payload<{ results: ApiCreatorTag[]; total: number }>(
        await client.get.api.web.creators(id).contentTags({
          query: {
            limit,
            offset,
            sortBy: by,
            sortOrder: order,
            withContent,
            contentCount,
          },
        })
      )

      if (!data) {
        throw new Error('Empty answer in creators.base.getContentTagsFx')
      }

      const results = (data.results || []).map(formatCreatorTag)

      return withTotal(results, data.total)
    }
  )

  // GET /api/web/creators/{id}/tv-show-tags
  creators.base.getTvShowTagsFx.use(
    async ({ id, limit, offset, by, order }) => {
      const data = payload<{ results: ApiCreatorTag[]; total: number }>(
        await client.get.api.web.creators(id).tvShowTags({
          query: { limit, offset, sortBy: by, sortOrder: order },
        })
      )

      if (!data) {
        throw new Error('Empty answer in creators.base.getTvShowTagsFx')
      }

      const results = (data.results || []).map(formatCreatorTag)

      return withTotal(results, data.total)
    }
  )

  // GET /api/web/creators/{id}/tv-show-tags/{genreId}/tv-shows
  creators.base.getTvShowsByTagFx.use(
    async ({ id, genreId, limit, offset, by, order }) => {
      const data = payload<{ results: ApiTvShow[]; total: number }>(
        await client.get.api.web
          .creators(id)
          .tvShowTags(genreId)
          .tvShows({
            query: { limit, offset, sortBy: by, sortOrder: order },
          })
      )

      if (!data) {
        throw new Error('Empty answer in creators.base.getTvShowsByTagFx')
      }

      const results = (data.results || []).map(tvShowFormat)

      return withTotal(results, data.total)
    }
  )
}
